import { ApiError } from '../../api.js';
import { UploadMessageView } from './UploadMessageView.js';

/**
 * A rejected upload, explained.
 *
 * The server distinguishes two kinds of failure and so does this: a 422 means
 * the FILE is wrong and carries `fix` (what to change) and `where` (which fund,
 * date or sheet) — that is shown as instructions. Anything else is our bug, and
 * says so plainly instead of implying the admin did something wrong.
 */
export function IngestFailure({ error }: { error: ApiError | Error | null }) {
  if (!error) return null;
  const rejected = error instanceof ApiError && error.status === 422;

  // Problems the server found and explained one by one: show every one of them.
  if (rejected && error.messages && error.messages.length > 0) {
    const n = error.messages.length;
    return (
      <div role="alert" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
        <p className="font-semibold text-amber-900">
          {n === 1 ? 'הקובץ לא נקלט' : `הקובץ לא נקלט — נמצאו ${n} בעיות, כולן כאן, כדי לתקן הכול בפעם אחת`}
        </p>
        <div className="mt-3 space-y-4">
          {error.messages.map((m, i) => (
            <div key={i} className={i > 0 ? 'border-t border-amber-200 pt-4' : ''}>
              <UploadMessageView message={m} tone="refused" />
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          לאחר התיקון בחרו את הקובץ המעודכן והעלו שוב — נתוני הלקוח הקיימים לא נפגעו.
        </p>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className={
        rejected
          ? 'mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4'
          : 'mt-4 rounded-lg border border-destructive/40 bg-destructive/10 p-4'
      }
    >
      <p className={rejected ? 'font-semibold text-amber-900' : 'font-semibold text-destructive'}>
        {rejected ? 'הקובץ לא נקלט' : 'הקליטה נכשלה'}
      </p>
      <p className="mt-1 text-sm text-foreground">{error.message}</p>

      {rejected && error.where && (
        <p className="mt-2 text-sm">
          <span className="text-muted-foreground">היכן: </span>
          <span dir="auto" className="font-medium">{error.where}</span>
        </p>
      )}

      {rejected && error.fix ? (
        <>
          <p className="mt-3 text-sm font-medium">מה לעשות</p>
          <p className="mt-1 text-sm text-foreground">{error.fix}</p>
          <p className="mt-3 text-sm text-muted-foreground">
            לאחר התיקון בחרו את הקובץ המעודכן והעלו שוב — נתוני הלקוח הקיימים לא נפגעו.
          </p>
        </>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          זו תקלה במערכת, לא בקובץ. נסו שוב, ואם זה חוזר — פנו לתמיכה עם ההודעה שלמעלה.
        </p>
      )}
    </div>
  );
}
